/*
 * Zomei Hero Slider
 * Bağımsız, kütüphanesiz hero slider mantığı.
 * - Her section instance kendi kök elemanı üzerinden çalışır (çakışma olmaz).
 * - Global scope kirletilmez (IIFE + WeakMap).
 * - Theme Editor: shopify:section:load / unload desteklenir.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-zhs-root]';
  var instances = new WeakMap();

  function ZomeiHeroSlider(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-zhs-viewport]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-zhs-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-zhs-dot]'));
    this.prevBtn = root.querySelector('[data-zhs-prev]');
    this.nextBtn = root.querySelector('[data-zhs-next]');
    this.liveRegion = root.querySelector('[data-zhs-live]');

    this.count = this.slides.length;
    this.current = 0;

    this.autoplay = root.dataset.zhsAutoplay === 'true';
    this.interval = parseInt(root.dataset.zhsInterval, 10) || 5000;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.timer = null;
    this.isHovered = false;
    this.isFocused = false;

    // Touch / swipe durumu
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.isSwiping = false;

    // Bağlı handler referansları (destroy için)
    this._handlers = {};

    this._init();
  }

  ZomeiHeroSlider.prototype._init = function () {
    if (this.count === 0) return;

    var self = this;

    // Tek slide varsa kontrolleri gizle ve autoplay'i kapat
    if (this.count <= 1) {
      this.autoplay = false;
      if (this.prevBtn) this.prevBtn.setAttribute('hidden', '');
      if (this.nextBtn) this.nextBtn.setAttribute('hidden', '');
    }

    // Ok butonları
    if (this.prevBtn) {
      this._handlers.prev = function () { self.prev(true); };
      this.prevBtn.addEventListener('click', this._handlers.prev);
    }
    if (this.nextBtn) {
      this._handlers.next = function () { self.next(true); };
      this.nextBtn.addEventListener('click', this._handlers.next);
    }

    // Noktalar
    this._handlers.dots = [];
    this.dots.forEach(function (dot, i) {
      var h = function () { self.goTo(i, true); };
      self._handlers.dots.push(h);
      dot.addEventListener('click', h);
    });

    // Klavye
    this._handlers.keydown = function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.prev(true); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.next(true); }
    };
    this.root.addEventListener('keydown', this._handlers.keydown);

    // Hover / focus -> autoplay geçici duraklat
    this._handlers.enter = function () { self.isHovered = true; self._stopTimer(); };
    this._handlers.leave = function () { self.isHovered = false; self._maybeStart(); };
    this._handlers.focusin = function () { self.isFocused = true; self._stopTimer(); };
    this._handlers.focusout = function () { self.isFocused = false; self._maybeStart(); };
    this.root.addEventListener('mouseenter', this._handlers.enter);
    this.root.addEventListener('mouseleave', this._handlers.leave);
    this.root.addEventListener('focusin', this._handlers.focusin);
    this.root.addEventListener('focusout', this._handlers.focusout);

    // Swipe / touch
    if (this.viewport && this.count > 1) {
      this._handlers.touchstart = function (e) { self._onTouchStart(e); };
      this._handlers.touchmove = function (e) { self._onTouchMove(e); };
      this._handlers.touchend = function (e) { self._onTouchEnd(e); };
      this.viewport.addEventListener('touchstart', this._handlers.touchstart, { passive: true });
      this.viewport.addEventListener('touchmove', this._handlers.touchmove, { passive: true });
      this.viewport.addEventListener('touchend', this._handlers.touchend, { passive: true });
    }

    // Sekme görünürlüğü
    this._handlers.visibility = function () {
      if (document.hidden) { self._stopTimer(); }
      else { self._maybeStart(); }
    };
    document.addEventListener('visibilitychange', this._handlers.visibility);

    // Başlangıç durumu
    this.goTo(0, false);
    this._maybeStart();
  };

  ZomeiHeroSlider.prototype.goTo = function (index, userInitiated) {
    if (this.count === 0) return;

    // Sonsuz döngü (wrap-around)
    var target = (index % this.count + this.count) % this.count;
    var previous = this.current;
    this.current = target;

    var direction = 'next';
    if (userInitiated) {
      // En kısa yön yerine gerçek yön: küçükse prev, büyükse next (wrap dahil)
      if (target === (previous - 1 + this.count) % this.count && this.count > 1) {
        direction = 'prev';
      } else if (target === (previous + 1) % this.count) {
        direction = 'next';
      } else {
        direction = target > previous ? 'next' : 'prev';
      }
    }
    this.root.setAttribute('data-zhs-dir', direction);

    this.slides.forEach(function (slide, i) {
      var isActive = i === target;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      // Aktif slide odaklanabilir kalsın; diğerlerindeki linkler tab dışına çıksın
      var focusables = slide.querySelectorAll('a, button');
      Array.prototype.forEach.call(focusables, function (el) {
        if (isActive) { el.removeAttribute('tabindex'); }
        else { el.setAttribute('tabindex', '-1'); }
      });
    });

    this.dots.forEach(function (dot, i) {
      var isActive = i === target;
      dot.classList.toggle('is-active', isActive);
      if (isActive) { dot.setAttribute('aria-current', 'true'); }
      else { dot.removeAttribute('aria-current'); }
    });

    if (this.liveRegion) {
      this.liveRegion.textContent = (target + 1) + ' / ' + this.count;
    }

    // Kullanıcı hareketi ise autoplay zamanlayıcısını sıfırla
    if (userInitiated) { this._maybeStart(); }
  };

  ZomeiHeroSlider.prototype.next = function (userInitiated) {
    this.goTo(this.current + 1, userInitiated);
  };

  ZomeiHeroSlider.prototype.prev = function (userInitiated) {
    this.goTo(this.current - 1, userInitiated);
  };

  // --- Autoplay ---
  ZomeiHeroSlider.prototype._maybeStart = function () {
    this._stopTimer();
    if (!this.autoplay || this.count <= 1) return;
    if (document.hidden) return;
    if (this.isHovered || this.isFocused) return;

    var self = this;
    this.timer = window.setInterval(function () {
      self.next(false);
    }, this.interval);
  };

  ZomeiHeroSlider.prototype._stopTimer = function () {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  };

  // --- Touch ---
  ZomeiHeroSlider.prototype._onTouchStart = function (e) {
    var t = e.changedTouches[0];
    this.touchStartX = t.clientX;
    this.touchStartY = t.clientY;
    this.touchDeltaX = 0;
    this.isSwiping = true;
    this._stopTimer();
  };

  ZomeiHeroSlider.prototype._onTouchMove = function (e) {
    if (!this.isSwiping) return;
    var t = e.changedTouches[0];
    this.touchDeltaX = t.clientX - this.touchStartX;
  };

  ZomeiHeroSlider.prototype._onTouchEnd = function () {
    if (!this.isSwiping) return;
    this.isSwiping = false;
    var threshold = 40;
    if (this.touchDeltaX <= -threshold) { this.next(true); }
    else if (this.touchDeltaX >= threshold) { this.prev(true); }
    else { this._maybeStart(); }
  };

  // --- Temizlik ---
  ZomeiHeroSlider.prototype.destroy = function () {
    this._stopTimer();
    var h = this._handlers;
    if (this.prevBtn && h.prev) this.prevBtn.removeEventListener('click', h.prev);
    if (this.nextBtn && h.next) this.nextBtn.removeEventListener('click', h.next);
    if (h.dots) {
      this.dots.forEach(function (dot, i) { dot.removeEventListener('click', h.dots[i]); });
    }
    if (h.keydown) this.root.removeEventListener('keydown', h.keydown);
    if (h.enter) this.root.removeEventListener('mouseenter', h.enter);
    if (h.leave) this.root.removeEventListener('mouseleave', h.leave);
    if (h.focusin) this.root.removeEventListener('focusin', h.focusin);
    if (h.focusout) this.root.removeEventListener('focusout', h.focusout);
    if (this.viewport) {
      if (h.touchstart) this.viewport.removeEventListener('touchstart', h.touchstart);
      if (h.touchmove) this.viewport.removeEventListener('touchmove', h.touchmove);
      if (h.touchend) this.viewport.removeEventListener('touchend', h.touchend);
    }
    if (h.visibility) document.removeEventListener('visibilitychange', h.visibility);
  };

  // --- Bootstrap ---
  function initAll(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var roots = container.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      instances.set(root, new ZomeiHeroSlider(root));
    });
  }

  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = scope.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      var inst = instances.get(root);
      if (inst) { inst.destroy(); instances.delete(root); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme Editor olayları
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
  document.addEventListener('shopify:section:reorder', function () { initAll(document); });
})();
